import React, { useState, useRef } from "react";
import { Button, message } from "antd";
import Draggable from "react-draggable";
import ScreenCapturePreview from "./ScreenCapturePreview";
import { createPortal } from "react-dom";

export default function ScreenCapturePro({ setFileList, onStart, onFinish }) {
  const [recording, setRecording] = useState(false);
  const [paused, setPaused] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [preview, setPreview] = useState(null);

  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const videoRef = useRef(null);
  const canvasDrawRef = useRef(null);
  const audioContextRef = useRef(null);
  const micStreamRef = useRef(null);
  const canvasStreamRef = useRef(null);

  const cleanupStreams = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    micStreamRef.current?.getTracks().forEach((t) => t.stop());
    micStreamRef.current = null;
    canvasStreamRef.current?.getTracks().forEach((t) => t.stop());
    canvasStreamRef.current = null;
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
  };

  const startRecording = async () => {
    try {
      onStart?.();
      const stream = await navigator.mediaDevices.getDisplayMedia({video: { displaySurface: "monitor" }, audio: true });
      streamRef.current = stream;

      const video = document.createElement("video");
      video.srcObject = stream;
      await video.play();
      videoRef.current = video;

      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      const draw = () => {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        requestAnimationFrame(draw);
      };
      draw();

      const canvasStream = canvas.captureStream(30);
      canvasStreamRef.current = canvasStream;

      const micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      micStreamRef.current = micStream;

      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;
      const destination = audioContext.createMediaStreamDestination();
      const micSource = audioContext.createMediaStreamSource(micStream);
      micSource.connect(destination);

      if (stream.getAudioTracks().length > 0) {
        const systemSource = audioContext.createMediaStreamSource(stream);
        systemSource.connect(destination);
      }

      const combinedStream = new MediaStream([
        ...canvasStream.getVideoTracks(),
        ...destination.stream.getAudioTracks(),
      ]);

      const recorder = new MediaRecorder(combinedStream, { mimeType: "video/webm;codecs=vp9,opus" });
      recorderRef.current = recorder;
      chunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "video/webm" });
        setPreview({ type: "video", blob, url: URL.createObjectURL(blob) });
        cleanupStreams();
        // onFinish?.();
      };
      recorder.start();
      setRecording(true);
    } catch {
      message.error("Recording permission denied");
    }
  };

  const togglePause = () => {
    if (!recorderRef.current) return;
    paused ? recorderRef.current.resume() : recorderRef.current.pause();
    setPaused(!paused);
  };

  const toggleMute = () => {
    const tracks = streamRef.current?.getAudioTracks() || [];
    tracks.forEach((track) => (track.enabled = isMuted));
    setIsMuted(!isMuted);
  };

  const stopRecording = () => {
    recorderRef.current?.stop();
    setRecording(false);
  };

  const takeScreenshot = async () => {
    try {
      onStart?.();
      const stream = await navigator.mediaDevices.getDisplayMedia({ video: { displaySurface: "monitor", preferCurrentTab: true }});
      const track = stream.getVideoTracks()[0];
      const imageCapture = new ImageCapture(track);
      const bitmap = await imageCapture.grabFrame();

      const canvas = document.createElement("canvas");
      canvas.width = bitmap.width;
      canvas.height = bitmap.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(bitmap, 0, 0);
      track.stop();

      canvas.toBlob((blob) => {
        setPreview({ type: "image", blob, url: URL.createObjectURL(blob) });
        // onFinish?.();
      });
    } catch {
      message.error("Screenshot failed");
    }
  };

  const handleConfirm = (blob) => {
    const ext = preview.type === "video" ? "webm" : "png";
    const file = new File([blob], `${preview.type}-${Date.now()}.${ext}`, { type: blob.type });
    setFileList((prev) => [...prev, { uid: Date.now(), name: file.name, originFileObj: file }]);
    setPreview(null);
    onFinish?.();
  };
const floatingControls = recording ? (
  <Draggable>
    <div style={{ position: "fixed", top: 80, left: 20, background: "#000", padding: 10, 
        borderRadius: 8, display: "flex", gap: 10, zIndex: 999999, cursor: "move" }}>
      <Button size="small" onClick={togglePause}> {paused ? "▶ Resume" : "⏸ Pause"}
      </Button>
      <Button size="small" onClick={toggleMute}> {isMuted ? "🔈 Unmute" : "🔇 Mute"}
      </Button>
      <Button danger size="small" onClick={stopRecording}> ⏹ Stop </Button>
    </div>
  </Draggable>
) : null;

  return (<>
    <div>
      {!recording && (
        <>
          <Button onClick={startRecording}> 🎥 Record Screen </Button>
          <Button style={{ marginLeft: 10 }} onClick={takeScreenshot}> 📸 Screenshot </Button>
        </>
      )}

      {/* {recording && (
        <Draggable>
          <div style={{ position: "fixed", top: 80, left: 20, background: "#000", padding: 10,
            borderRadius: 8, display: "flex", gap: 10, zIndex: 9999, cursor: "move" }}>
            <Button size="small" onClick={togglePause}> {paused ? "▶ Resume" : "⏸ Pause"} </Button>
            <Button size="small" onClick={toggleMute}> {isMuted ? "🔈 Unmute" : "🔇 Mute"} </Button>
            <Button danger size="small" onClick={stopRecording}> ⏹ Stop </Button>
          </div>
        </Draggable>
      )} */}

      <ScreenCapturePreview
        preview={preview}
        onClose={() => {
          setPreview(null)
          onFinish?.();}}
        onSave={handleConfirm}
      />
    </div>
    {createPortal(floatingControls, document.body)}
    </>
  );
}